import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchRecommendedArtifacts, createFromPrime } from '../../store/slices/ArtifactSlice';
import { useArtifactTree } from '../../contexts';
import { CompanyArtifactId } from '../../types/artifacts-branded';
import styles from './RecommendedArtifacts.module.css';

interface CreateArtifactDialogProps {
  isOpen: boolean;
  defaultName: string;
  onConfirm: (name: string) => void;
  onCancel: () => void;
}

const CreateArtifactDialog: React.FC<CreateArtifactDialogProps> = ({
  isOpen,
  defaultName,
  onConfirm,
  onCancel,
}) => {
  const [name, setName] = useState(defaultName);

  if (!isOpen) return null;

  return (
    <div className={styles.dialogOverlay}>
      <div className={styles.dialogContent}>
        <h3>Create Krysalis Artifact</h3>
        <div className={styles.dialogForm}>
          <label>
            Name:
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </label>
        </div>
        <div className={styles.dialogButtons}>
          <button onClick={() => onConfirm(name)}>Create</button>
          <button onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

interface RecommendedArtifactsProps {
  artifactId: CompanyArtifactId;
}

export const RecommendedArtifacts: React.FC<RecommendedArtifactsProps> = ({ artifactId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedTree } = useArtifactTree();
  const recommendations = useSelector(
    (state: RootState) => state.artifacts.recommendedPeerArtifacts
  );

  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    prime_artifact_id?: string;
    defaultName?: string;
  }>({
    isOpen: false,
  });

  useEffect(() => {
    const loadRecommendations = async () => {
      await dispatch(fetchRecommendedArtifacts(artifactId));
    };
    void loadRecommendations();
  }, [dispatch, artifactId]);

  const handleCreateClick = (prime_artifact_id: string, defaultName: string) => {
    setDialogState({
      isOpen: true,
      prime_artifact_id,
      defaultName,
    });
  };

  const handleCreateConfirm = async (_name: string) => {
    if (!dialogState.prime_artifact_id || !selectedTree) return;

    try {
      await dispatch(
        createFromPrime({
          prime_artifact_id: dialogState.prime_artifact_id,
          treeId: selectedTree.id,
          parentRef: artifactId,
        })
      ).unwrap();
      setDialogState({ isOpen: false });
    } catch (error) {
      console.error('Failed to create Krysalis artifact:', error);
    }
  };

  const handleCreateCancel = () => {
    setDialogState({ isOpen: false });
  };

  if (!recommendations.length) {
    return null;
  }

  return (
    <div className={styles.recommendedArtifacts}>
      <h4>Recommended Artifacts</h4>
      {recommendations.map((rec) => (
        <div key={rec.source_artifact_id} className={styles.recommendationItem}>
          <div className={styles.recommendationInfo}>
            <span className={styles.name}>{rec.name}</span>
            <span className={styles.type}>{rec.relationship_type}</span>
            <span className={styles.reason}>{rec.reason}</span>
          </div>
          <button onClick={() => handleCreateClick(rec.source_artifact_id, rec.name)}>
            Create Krysalis Artifact
          </button>
        </div>
      ))}

      <CreateArtifactDialog
        isOpen={dialogState.isOpen}
        defaultName={dialogState.defaultName || ''}
        onConfirm={handleCreateConfirm}
        onCancel={handleCreateCancel}
      />
    </div>
  );
};
